import { api } from '../api/endpoints'
import { channelMessages, db } from '../db/db'
import { storeHistory, upsertUsers } from './applyEvents'
import { syncAll } from './syncEngine'

const loading = new Map<string, Promise<number>>()

/** GET /api/channels/{id}/messages?beforeSeq=<oldest local seq> and store the page. */
export function loadOlderMessages(channelId: string): Promise<number> {
  const existing = loading.get(channelId)
  if (existing) return existing
  const task = (async () => {
    const channel = await db.channels.get(channelId)
    if (!channel) return 0
    const oldest = await channelMessages(channelId).first()
    const beforeSeq = oldest ? oldest.seq : channel.headSeq + 1
    if (beforeSeq <= 1) {
      await db.channels.update(channelId, { hasMore: false })
      return 0
    }
    const page = await api.messages(channelId, beforeSeq, 50)
    await upsertUsers(page.users)
    return storeHistory(channelId, page.messages, page.hasMore)
  })().finally(() => loading.delete(channelId))
  loading.set(channelId, task)
  return task
}

/**
 * Page back until a message is available locally (search result, reply target).
 * `seq` bounds the search when known. Pages are contiguous so no gaps are created.
 */
export async function ensureMessageLoaded(
  channelId: string,
  messageId: string,
  seq?: number,
  maxPages = 40,
): Promise<boolean> {
  for (let page = 0; page <= maxPages; page++) {
    if (await db.messages.get(messageId)) return true
    const channel = await db.channels.get(channelId)
    if (channel && channel.version === null) {
      // Placeholder waiting for its snapshot (admin view, force resync): sync first.
      await syncAll()
      continue
    }
    const oldest = await channelMessages(channelId).first()
    const pastTarget = seq !== undefined && oldest !== undefined && oldest.seq <= seq
    if (!channel || !channel.hasMore || pastTarget) {
      // A page may have been committed between the checks above.
      return Boolean(await db.messages.get(messageId))
    }
    await loadOlderMessages(channelId)
  }
  return Boolean(await db.messages.get(messageId))
}
